import type { Entity } from '@backstage/catalog-model';

/**
 * Helpers for reading the fields of the AWS-account and environment Resource
 * entities generated from onboarding.hcl (see docs/catalog-entity-model.md).
 *
 * AWS-account Resource (spec.type === 'aws-account'):
 *   metadata.annotations['amazonaws.com/account-id']  # 12-digit account id
 *   spec.dependsOn: [domain:default/<d>, resource:default/env-<env>]
 *
 * Environment Resource (spec.type === 'environment'):
 *   metadata.name === 'env-<env>'
 */

export const AWS_ACCOUNT_ID_ANNOTATION = 'amazonaws.com/account-id';

/** True for a Resource entity of the given spec.type (case-insensitive). */
function isResourceOfType(entity: Entity, type: string): boolean {
  if (entity.kind.toLocaleLowerCase('en-US') !== 'resource') return false;
  const specType = (entity.spec as Record<string, unknown> | undefined)?.type;
  return typeof specType === 'string' && specType === type;
}

export function isAwsAccountEntity(entity: Entity): boolean {
  return isResourceOfType(entity, 'aws-account');
}

export function isEnvironmentEntity(entity: Entity): boolean {
  return isResourceOfType(entity, 'environment');
}

/** The 12-digit AWS account id from the annotation, if present. */
export function getAwsAccountId(entity: Entity): string | undefined {
  const value = entity.metadata.annotations?.[AWS_ACCOUNT_ID_ANNOTATION];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The environment an aws-account belongs to, derived from its
 * `resource:default/env-<env>` dependency. Returns e.g. `prod`, or undefined
 * for a non-environment-scoped account.
 */
export function getAwsAccountEnvironment(entity: Entity): string | undefined {
  const dependsOn = (entity.spec as Record<string, unknown> | undefined)
    ?.dependsOn;
  if (!Array.isArray(dependsOn)) return undefined;
  for (const ref of dependsOn) {
    if (typeof ref !== 'string') continue;
    const match = ref.match(/(?:resource:)?(?:default\/)?env-([a-z0-9-]+)$/i);
    if (match) return match[1];
  }
  return undefined;
}
